import React, { useState } from 'react';
import { Microscope, Link2, Mail, FileText, Loader2, Brain, Cpu } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { GlassCard } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { explainLink, explainEmail, explainArticle } from '@/lib/mlClient';
import { MLFeatureTable } from '../MLInsights';
import { useApp } from '@/context/AppContext';
import { cn } from '@/lib/utils';

const TYPES = [
  { id: 'url', label: 'URL', icon: Link2 },
  { id: 'email', label: 'Email', icon: Mail },
  { id: 'article', label: 'Article', icon: FileText },
];

export const MLExplainability = () => {
  const [type, setType] = useState('url');
  const [url, setUrl] = useState('');
  const [email, setEmail] = useState('');
  const [content, setContent] = useState('');
  const [text, setText] = useState('');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const { addVerification } = useApp();

  const run = async () => {
    setLoading(true);
    setResult(null);
    let res;
    if (type === 'url') res = await explainLink(url);
    else if (type === 'email') res = await explainEmail(email, content);
    else res = await explainArticle(text, '');
    setResult(res && res.ok ? res.analysis : { unavailable: true });
    setLoading(false);
    addVerification({ type: 'phishing-lab', input: `[ml-explain:${type}]`, verdict: 'Analyzed', result: { mode: 'defender' } });
  };

  const canRun = type === 'url' ? url.trim() : type === 'email' ? email.trim() : text.trim().length >= 50;

  return (
    <div className="space-y-5" data-testid="ml-explainability">
      <GlassCard className="p-4 sm:p-6">
        <div className="flex items-center gap-2 mb-4">
          <Microscope className="w-5 h-5 text-primary" />
          <h3 className="font-display text-lg font-bold">ML Explainability</h3>
        </div>
        <p className="text-sm text-muted-foreground mb-4">
          See the exact feature values and top contributing terms the backend scikit-learn models compute for any input.
        </p>

        <div className="flex gap-2 mb-4">
          {TYPES.map(({ id, label, icon: Icon }) => (
            <button key={id} onClick={() => { setType(id); setResult(null); }}
              data-testid={`ml-explain-type-${id}`}
              className={cn('px-3 py-2 rounded-lg text-xs font-medium transition-colors flex items-center gap-1.5',
                type === id ? 'bg-primary text-primary-foreground' : 'bg-muted hover:bg-muted/70 text-muted-foreground')}>
              <Icon className="w-3.5 h-3.5" /> {label}
            </button>
          ))}
        </div>

        {type === 'url' && (
          <Input placeholder="http://g00gle-login.xyz/verify" value={url} onChange={(e) => setUrl(e.target.value)}
            className="h-11" data-testid="ml-explain-url-input" />
        )}
        {type === 'email' && (
          <div className="space-y-3">
            <Input placeholder="sender@example.com" value={email} onChange={(e) => setEmail(e.target.value)}
              className="h-11" data-testid="ml-explain-email-input" />
            <Textarea placeholder="Paste email body (optional)" value={content} onChange={(e) => setContent(e.target.value)}
              className="min-h-[100px]" data-testid="ml-explain-email-content" />
          </div>
        )}
        {type === 'article' && (
          <Textarea placeholder="Paste article text (min 50 chars)..." value={text} onChange={(e) => setText(e.target.value)}
            className="min-h-[140px]" data-testid="ml-explain-article-input" />
        )}

        <Button onClick={run} disabled={!canRun || loading} variant="gradient"
          className="w-full h-11 mt-4" data-testid="ml-explain-run">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Microscope className="w-4 h-4 mr-2" /> Extract Features</>}
        </Button>
      </GlassCard>

      {result && result.unavailable && (
        <GlassCard className="p-4 border-destructive/30 border">
          <p className="text-sm text-destructive">Model service unavailable. Please try again.</p>
        </GlassCard>
      )}

      {result && !result.unavailable && (
        <div className="space-y-4" data-testid="ml-explain-result">
          <GlassCard className="p-4 flex flex-wrap items-center gap-3">
            <Badge variant={result.source === 'model' ? 'safe' : 'suspicious'} className="text-[10px]">
              {result.source === 'model' ? <Brain className="w-3 h-3 mr-1" /> : <Cpu className="w-3 h-3 mr-1" />}
              {result.source === 'model' ? 'ML model' : 'Heuristic fallback'}
            </Badge>
            {'probMalicious' in result && result.probMalicious != null && (
              <span className="text-xs font-mono">P(malicious) = {result.probMalicious}</span>)}
            {'probPhishing' in result && result.probPhishing != null && (
              <span className="text-xs font-mono">P(phishing) = {result.probPhishing}</span>)}
            {'probFake' in result && result.probFake != null && (
              <span className="text-xs font-mono">P(fake) = {result.probFake}</span>)}
            {result.criticalSignals && result.criticalSignals.length > 0 && (
              <Badge variant="danger" className="text-[10px]">{result.criticalSignals.join(', ').replace(/_/g, ' ')}</Badge>)}
          </GlassCard>

          {type === 'url' && result.features && (
            <>
              <MLFeatureTable title="Computed URL features" data={result.features} />
              {result.featureImportances && Object.keys(result.featureImportances).length > 0 && (
                <MLFeatureTable title="Global model feature importances" data={result.featureImportances} />
              )}
            </>
          )}

          {type === 'email' && (
            <>
              {result.topTerms && result.topTerms.length > 0 && (
                <GlassCard className="p-4">
                  <h4 className="text-sm font-semibold mb-2">Top contributing terms</h4>
                  <div className="flex flex-wrap gap-1.5">
                    {result.topTerms.map((t, i) => (
                      <span key={i} className={cn('px-2 py-1 rounded text-[11px] font-mono',
                        t.weight > 0 ? 'bg-destructive/10 text-destructive' : 'bg-success/10 text-success')}>
                        {t.term} ({t.weight > 0 ? '+' : ''}{t.weight})
                      </span>
                    ))}
                  </div>
                </GlassCard>
              )}
              {result.structuralFeatures && (
                <MLFeatureTable title="Structural features (value / availability / coefficient)" data={result.structuralFeatures} />
              )}
            </>
          )}

          {type === 'article' && result.topTerms && (
            <GlassCard className="p-4">
              <h4 className="text-sm font-semibold mb-2">Top contributing terms</h4>
              <div className="flex flex-wrap gap-1.5">
                {result.topTerms.map((t, i) => (
                  <span key={i} className={cn('px-2 py-1 rounded text-[11px] font-mono',
                    t.weight > 0 ? 'bg-destructive/10 text-destructive' : 'bg-success/10 text-success')}>
                    {t.term} ({t.weight > 0 ? '+' : ''}{t.weight})
                  </span>
                ))}
              </div>
              <p className="text-[11px] text-muted-foreground mt-3">{result.caveat}</p>
            </GlassCard>
          )}
        </div>
      )}
    </div>
  );
};

export default MLExplainability;
